// RecMakeupBrowsLashes.jsx - Professional Makeup & Brow Guide
import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18n";
import "./recommended.css";

const recommendedItems = [
  { title: "hairTrendsStylingGuide", image: "images/hair.jpg", slug: "hair-&-nail-salon" },
  { title: "mensGroomingMustacheStyles", image: "images/barbershop.jpg", slug: "barbershop" },
  { title: "tattooIdeasArtistGuide", image: "images/tattoo.jpg", slug: "tattoo-&-piercing-shops" },
  { title: "hairRemovalSkincare", image: "images/hair1.jpg", slug: "hair-removal" },
  { title: "browLashEnhancement", image: "images/brows.jpg", slug: "makeup-&-brows-&-lashes" },
  { title: "spaWellnessTreatments", image: "images/massage.jpg", slug: "massage-&-aesthetic-medicine" },
];

export default function RecMakeupBrowsLashes() {
  const { t, lang } = useI18n();
  
  const handleTocClick = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  return (
    <div className="rec-page" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <details className="rec-mobile-panel" role="navigation" aria-label="Recommended quick links">
        <summary>
          {t('recommendedServices')}
                    <span className="rec-plus" aria-hidden="true">+</span>

          <span className="rec-plus" aria-hidden="true"></span>
        </summary>
        <ul className="rec-quick-list">
          {recommendedItems.map((it) => (
            <li key={it.slug}>
              <Link to={`/rec/${it.slug}`}>{t(it.title)}</Link>
            </li>
          ))}
        </ul>
      </details>

      <aside className="rec-left" aria-label="Recommended Services">
        <h2 className="rec-left__title">{t('recommendedServices')}</h2>
        <ul className="rec-left__list">
          {recommendedItems.map((it) => (
            <li key={it.slug} className="rec-left__item">
              <Link to={`/rec/${it.slug}`} className="rec-left__link">
                <img className="rec-left__thumb" src={`/${it.image}`} alt="" />
                <span className="rec-left__label">{t(it.title)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </aside>

      <article className="rec-article">
        <header className="rec-hero">
          <h1>{t('completeMakeupBrowGuide')}</h1>
          <p className="rec-meta">{t('professionalBeautyGuide')}</p>
          <p className="rec-dek">
            {t('recMakeupIntro')}
          </p>
          <div className="rec-cta-row">
            <Link className="btn primary" to="/service/makeup-&-brows-&-lashes">{t('bookService')}</Link>
            <Link className="btn outline" to="/service/makeup-&-brows-&-lashes">{t('viewAllServices')}</Link>
          </div>
        </header>

        <nav className="rec-toc" aria-label="Table of Contents">
          <h2>{t('tableOfContents')}</h2>
          <div className="rec-toc-summary">
            <p><strong>4 {t('sections')}</strong> • <strong>55 {t('minTotalRead')}</strong></p>
          </div>
          <ul className="rec-toc-list">
            <li className="rec-toc-item">
              <a href="#brow-shaping" className="rec-toc-link" onClick={(e) => handleTocClick(e, 'brow-shaping')}>
                <div className="rec-toc-content">
                  <h3 className="rec-toc-title">{t('professionalBrowShaping')}</h3>
                  <p className="rec-toc-description">{t('masterBrowShapingTechniques')}</p>
                  <div className="rec-toc-meta">
                    <span className="rec-toc-time">12 {t('minRead')}</span>
                    <span className="rec-toc-difficulty rec-toc-difficulty--beginner">{t('beginner')}</span>
                  </div>
                </div>
              </a>
            </li>
            <li className="rec-toc-item">
              <a href="#lash-enhancement" className="rec-toc-link" onClick={(e) => handleTocClick(e, 'lash-enhancement')}>
                <div className="rec-toc-content">
                  <h3 className="rec-toc-title">{t('lashEnhancementDramaticEye')}</h3>
                  <p className="rec-toc-description">{t('discoverProfessionalLashTreatments')}</p>
                  <div className="rec-toc-meta">
                    <span className="rec-toc-time">10 {t('minRead')}</span>
                    <span className="rec-toc-difficulty rec-toc-difficulty--intermediate">{t('intermediate')}</span>
                  </div>
                </div>
              </a>
            </li>
            <li className="rec-toc-item">
              <a href="#makeup-basics" className="rec-toc-link" onClick={(e) => handleTocClick(e, 'makeup-basics')}>
                <div className="rec-toc-content">
                  <h3 className="rec-toc-title">{t('professionalMakeupFoundation')}</h3>
                  <p className="rec-toc-description">{t('buildSolidFoundationMakeup')}</p>
                  <div className="rec-toc-meta">
                    <span className="rec-toc-time">15 {t('minRead')}</span>
                    <span className="rec-toc-difficulty rec-toc-difficulty--beginner">{t('beginner')}</span>
                  </div>
                </div>
              </a>
            </li>
            <li className="rec-toc-item">
              <a href="#eye-makeup" className="rec-toc-link" onClick={(e) => handleTocClick(e, 'eye-makeup')}>
                <div className="rec-toc-content">
                  <h3 className="rec-toc-title">{t('advancedEyeMakeupArtistry')}</h3>
                  <p className="rec-toc-description">{t('masterAdvancedEyeMakeup')}</p>
                  <div className="rec-toc-meta">
                    <span className="rec-toc-time">18 {t('minRead')}</span>
                    <span className="rec-toc-difficulty rec-toc-difficulty--advanced">{t('advanced')}</span>
                  </div>
                </div>
              </a>
            </li>
          </ul>
        </nav>

        <section id="brow-shaping" className="rec-section">
          <div className="rec-section-header">
            <div className="rec-section-info">
              <h3>{t('professionalBrowShaping')}</h3>
              <div className="rec-section-details">
                <span className="rec-section-time">12 {t('minRead')}</span>
                <span className="rec-section-difficulty rec-section-difficulty--beginner">{t('beginner')}</span>
              </div>
            </div>
          </div>
          <div className="rec-section-intro">
            <p>{t('recBrowShapingIntro')}</p>
          </div>
          <div className="rec-quick-tips">
            <h4>{t('quickTips')}</h4>
            <ul className="rec-tips-list">
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recBrowShapingQuickTip1')}</span><span className="rec-tip-icon">💡</span></li>
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recBrowShapingQuickTip2')}</span><span className="rec-tip-icon">💡</span></li>
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recBrowShapingQuickTip3')}</span><span className="rec-tip-icon">💡</span></li>
            </ul>
          </div>
          <div className="rec-section-body">
            <div className="rec-section-content">
              <div>
              <p>{t('recBrowShapingContent1')}</p>
              
              <h4>{t('recBrowShapingContent2')}</h4>
              
              <h5>{t('recBrowShapingContent3')}</h5>
              <p>
                <strong>{t('recBrowShapingContent4')}</strong> {t('recBrowShapingContent5')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent6')}</strong> {t('recBrowShapingContent7')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent8')}</strong> {t('recBrowShapingContent9')}
              </p>
              
              <h5>{t('recBrowShapingContent10')}</h5>
              <p>
                <strong>{t('recBrowShapingContent4')}</strong> {t('recBrowShapingContent11')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent6')}</strong> {t('recBrowShapingContent12')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent8')}</strong> {t('recBrowShapingContent13')}
              </p>
              
              <h4>{t('recBrowShapingContent14')}</h4>
              
              <h5>{t('recBrowShapingContent15')}</h5>
              <p>
                <strong>{t('recBrowShapingContent16')}</strong> {t('recBrowShapingContent17')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent18')}</strong> {t('recBrowShapingContent19')}
              </p>
              
              <h5>{t('recBrowShapingContent20')}</h5>
              <p>
                <strong>{t('recBrowShapingContent16')}</strong> {t('recBrowShapingContent21')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent18')}</strong> {t('recBrowShapingContent22')}
              </p>
              
              <h4>{t('recBrowShapingContent23')}</h4>
              
              <h5>{t('recBrowShapingContent24')}</h5>
              <ul>
                <li><strong>{t('recBrowShapingContent25')}</strong> {t('recBrowShapingContent26')}</li>
                <li><strong>{t('recBrowShapingContent27')}</strong> {t('recBrowShapingContent28')}</li>
                <li><strong>{t('recBrowShapingContent29')}</strong> {t('recBrowShapingContent30')}</li>
              </ul>
              
              <h5>{t('recBrowShapingContent31')}</h5>
              <ul>
                <li><strong>{t('recBrowShapingContent25')}</strong> {t('recBrowShapingContent32')}</li>
                <li><strong>{t('recBrowShapingContent27')}</strong> {t('recBrowShapingContent33')}</li>
                <li><strong>{t('recBrowShapingContent34')}</strong> {t('recBrowShapingContent35')}</li>
              </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="lash-enhancement" className="rec-section">
          <div className="rec-section-header">
            <div className="rec-section-info">
              <h3>{t('lashEnhancementDramaticEye')}</h3>
              <div className="rec-section-details">
                <span className="rec-section-time">10 {t('minRead')}</span>
                <span className="rec-section-difficulty rec-section-difficulty--intermediate">{t('intermediate')}</span>
              </div>
            </div>
          </div>
          <div className="rec-section-intro">
            <p>{t('recLashEnhancementIntro')}</p>
          </div>
          <div className="rec-quick-tips">
            <h4>{t('quickTips')}</h4>
            <ul className="rec-tips-list">
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recLashEnhancementQuickTip1')}</span><span className="rec-tip-icon">💡</span></li>
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recLashEnhancementQuickTip2')}</span><span className="rec-tip-icon">💡</span></li>
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recLashEnhancementQuickTip3')}</span><span className="rec-tip-icon">💡</span></li>
            </ul>
          </div>
          <div className="rec-section-body">
            <div className="rec-section-content">
              <div>
              <p>{t('recLashEnhancementContent1')}</p>
              
              <h4>{t('recLashEnhancementContent2')}</h4>
              
              <h5>{t('recLashEnhancementContent3')}</h5>
              <p>
                <strong>{t('recBrowShapingContent4')}</strong> {t('recLashEnhancementContent4')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent6')}</strong> {t('recLashEnhancementContent5')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent8')}</strong> {t('recLashEnhancementContent6')}
              </p>
              
              <h5>{t('recLashEnhancementContent7')}</h5>
              <p>
                <strong>{t('recBrowShapingContent4')}</strong> {t('recLashEnhancementContent8')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent6')}</strong> {t('recLashEnhancementContent9')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent8')}</strong> {t('recLashEnhancementContent10')}
              </p>
              
              <h4>{t('recLashEnhancementContent11')}</h4>
              
              <h5>{t('recLashEnhancementContent12')}</h5>
              <p>
                <strong>{t('recLashEnhancementContent13')}</strong> {t('recLashEnhancementContent14')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent6')}</strong> {t('recLashEnhancementContent15')}
              </p>
              
              <h5>{t('recLashEnhancementContent16')}</h5>
              <p>
                <strong>{t('recLashEnhancementContent13')}</strong> {t('recLashEnhancementContent17')}
              </p>
              <p>
                <strong>{t('recBrowShapingContent6')}</strong> {t('recLashEnhancementContent18')}
              </p>
              
              <h4>{t('recLashEnhancementContent19')}</h4>
              
              <h5>{t('recLashEnhancementContent20')}</h5>
              <ul>
                <li><strong>{t('recLashEnhancementContent21')}</strong> {t('recLashEnhancementContent22')}</li>
                <li><strong>{t('recLashEnhancementContent23')}</strong> {t('recLashEnhancementContent24')}</li>
                <li><strong>{t('recLashEnhancementContent25')}</strong> {t('recLashEnhancementContent26')}</li>
                <li><strong>{t('recLashEnhancementContent27')}</strong> {t('recLashEnhancementContent28')}</li>
              </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="makeup-basics" className="rec-section">
          <div className="rec-section-header">
            <div className="rec-section-info">
              <h3>{t('professionalMakeupFoundation')}</h3>
              <div className="rec-section-details">
                <span className="rec-section-time">15 {t('minRead')}</span>
                <span className="rec-section-difficulty rec-section-difficulty--beginner">{t('beginner')}</span>
              </div>
            </div>
          </div>
          <div className="rec-section-intro">
            <p>{t('recMakeupFoundationIntro')}</p>
          </div>
          <div className="rec-quick-tips">
            <h4>{t('quickTips')}</h4>
            <ul className="rec-tips-list">
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recMakeupFoundationQuickTip1')}</span><span className="rec-tip-icon">💡</span></li>
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recMakeupFoundationQuickTip2')}</span><span className="rec-tip-icon">💡</span></li>
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recMakeupFoundationQuickTip3')}</span><span className="rec-tip-icon">💡</span></li>
            </ul>
          </div>
          <div className="rec-section-body">
            <div className="rec-section-content">
              <div>
              <p>{t('recMakeupFoundationContent1')}</p>
              
              <h4>{t('recMakeupFoundationContent2')}</h4>
              
              <h5>{t('recMakeupFoundationContent3')}</h5>
              <p>
                <strong>{t('recMakeupFoundationContent4')}</strong> {t('recMakeupFoundationContent5')}
              </p>
              <p>
                <strong>{t('recMakeupFoundationContent6')}</strong> {t('recMakeupFoundationContent7')}
              </p>
              <p>
                <strong>{t('recMakeupFoundationContent8')}</strong> {t('recMakeupFoundationContent9')}
              </p>
              
              <h4>{t('recMakeupFoundationContent10')}</h4>
              
              <h5>{t('recMakeupFoundationContent11')}</h5>
              <ul>
                <li><strong>{t('recMakeupFoundationContent12')}</strong> {t('recMakeupFoundationContent13')}</li>
                <li><strong>{t('recMakeupFoundationContent14')}</strong> {t('recMakeupFoundationContent15')}</li>
                <li><strong>{t('recMakeupFoundationContent16')}</strong> {t('recMakeupFoundationContent17')}</li>
                <li><strong>{t('recMakeupFoundationContent18')}</strong> {t('recMakeupFoundationContent19')}</li>
              </ul>
              
              <h5>{t('recMakeupFoundationContent20')}</h5>
              <ul>
                <li><strong>{t('recMakeupFoundationContent21')}</strong> {t('recMakeupFoundationContent22')}</li>
                <li><strong>{t('recMakeupFoundationContent23')}</strong> {t('recMakeupFoundationContent24')}</li>
                <li><strong>{t('recMakeupFoundationContent25')}</strong> {t('recMakeupFoundationContent26')}</li>
                <li><strong>{t('recMakeupFoundationContent27')}</strong> {t('recMakeupFoundationContent28')}</li>
              </ul>
              
              <h4>{t('recMakeupFoundationContent29')}</h4>
              
              <h5>{t('recMakeupFoundationContent30')}</h5>
              <ul>
                <li><strong>{t('recMakeupFoundationContent31')}</strong> {t('recMakeupFoundationContent32')}</li>
                <li><strong>{t('recMakeupFoundationContent33')}</strong> {t('recMakeupFoundationContent34')}</li>
                <li><strong>{t('recMakeupFoundationContent35')}</strong> {t('recMakeupFoundationContent36')}</li>
                <li><strong>{t('recMakeupFoundationContent37')}</strong> {t('recMakeupFoundationContent38')}</li>
              </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="eye-makeup" className="rec-section">
          <div className="rec-section-header">
            <div className="rec-section-info">
              <h3>{t('advancedEyeMakeupArtistry')}</h3>
              <div className="rec-section-details">
                <span className="rec-section-time">18 {t('minRead')}</span>
                <span className="rec-section-difficulty rec-section-difficulty--advanced">{t('advanced')}</span>
              </div>
            </div>
          </div>
          <div className="rec-section-intro">
            <p>{t('recEyeMakeupIntro')}</p>
          </div>
          <div className="rec-quick-tips">
            <h4>{t('quickTips')}</h4>
            <ul className="rec-tips-list">
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recEyeMakeupQuickTip1')}</span><span className="rec-tip-icon">💡</span></li>
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recEyeMakeupQuickTip2')}</span><span className="rec-tip-icon">💡</span></li>
              <li className="rec-tip-item"><span className="rec-tip-text">{t('recEyeMakeupQuickTip3')}</span><span className="rec-tip-icon">💡</span></li>
            </ul>
          </div>
          <div className="rec-section-body">
            <div className="rec-section-content">
              <div>
              <p>{t('recEyeMakeupContent1')}</p>
              
              <h4>{t('recEyeMakeupContent2')}</h4>
              
              <h5>{t('recEyeMakeupContent3')}</h5>
              <p>
                <strong>{t('recEyeMakeupContent4')}</strong> {t('recEyeMakeupContent5')}
              </p>
              <p>
                <strong>{t('recEyeMakeupContent6')}</strong> {t('recEyeMakeupContent7')}
              </p>
              <p>
                <strong>{t('recEyeMakeupContent8')}</strong> {t('recEyeMakeupContent9')}
              </p>
              <p>
                <strong>{t('recEyeMakeupContent10')}</strong> {t('recEyeMakeupContent11')}
              </p>
              
              <h4>{t('recEyeMakeupContent12')}</h4>
              
              <h5>{t('recEyeMakeupContent13')}</h5>
              <ul>
                <li><strong>{t('recEyeMakeupContent14')}</strong> {t('recEyeMakeupContent15')}</li>
                <li><strong>{t('recEyeMakeupContent16')}</strong> {t('recEyeMakeupContent17')}</li>
                <li><strong>{t('recEyeMakeupContent18')}</strong> {t('recEyeMakeupContent19')}</li>
                <li><strong>{t('recEyeMakeupContent20')}</strong> {t('recEyeMakeupContent21')}</li>
              </ul>
              
              <h5>{t('recEyeMakeupContent22')}</h5>
              <ul>
                <li><strong>{t('recEyeMakeupContent23')}</strong> {t('recEyeMakeupContent24')}</li>
                <li><strong>{t('recEyeMakeupContent25')}</strong> {t('recEyeMakeupContent26')}</li>
                <li><strong>{t('recEyeMakeupContent27')}</strong> {t('recEyeMakeupContent28')}</li>
                <li><strong>{t('recEyeMakeupContent29')}</strong> {t('recEyeMakeupContent30')}</li>
              </ul>
              
              <h4>{t('recEyeMakeupContent31')}</h4>
              
              <h5>{t('recEyeMakeupContent32')}</h5>
              <ul>
                <li><strong>{t('recEyeMakeupContent33')}</strong> {t('recEyeMakeupContent34')}</li>
                <li><strong>{t('recEyeMakeupContent35')}</strong> {t('recEyeMakeupContent36')}</li>
                <li><strong>{t('recEyeMakeupContent37')}</strong> {t('recEyeMakeupContent38')}</li>
                <li><strong>{t('recEyeMakeupContent39')}</strong> {t('recEyeMakeupContent40')}</li>
              </ul>
              
              <h4>{t('recEyeMakeupContent41')}</h4>
              
              <h5>{t('recEyeMakeupContent42')}</h5>
              <ul>
                <li><strong>{t('recEyeMakeupContent43')}</strong> {t('recEyeMakeupContent44')}</li>
                <li><strong>{t('recEyeMakeupContent45')}</strong> {t('recEyeMakeupContent46')}</li>
                <li><strong>{t('recEyeMakeupContent47')}</strong> {t('recEyeMakeupContent48')}</li>
                <li><strong>{t('recEyeMakeupContent49')}</strong> {t('recEyeMakeupContent50')}</li>
              </ul>
              </div>
            </div>
          </div>
        </section>

        <footer className="rec-footer">
          <h3>{t('readyMasterProfessionalMakeup')}</h3>
          <p>
            {t('dontWaitLearnMakeup')}
          </p>
          <div className="rec-cta-row">
            <Link className="btn primary" to="/service/makeup-&-brows-&-lashes">{t('bookMakeupService')}</Link>
            <Link className="btn outline" to="/service/makeup-&-brows-&-lashes">{t('bookBrowService')}</Link>
          </div>
        </footer>
      </article>
    </div>
  );
}